/**
 * The Stippl trip an itinerary article links to, read out of the article itself.
 *
 * The marketing team puts the article's template trip in the body as
 * `https://www.stippl.io/routepreview/<24-hex tripId>?utm_medium=website&utm_source=blog&utm_campaign=blogtrips`,
 * either as the `href` of the `route-cta` block or as a markdown link inside a paragraph / list.
 * `ItineraryArticle` reads it back here so the sidebar's map link and CTA open that same trip
 * instead of being decoration.
 *
 * ONLY THE ID FORM COUNTS. Some articles still carry the old name-based `/stippl_/trip/<name>`
 * URL, which breaks the moment the trip is renamed, so it is deliberately not matched here: such
 * an article gets no trip link (the sidebar CTA falls back to the sign-up modal) rather than one
 * that can die silently.
 */

import type { ArticleData, ArticleNode } from "components/discover/articleData";

/** An absolute http(s) URL inside prose or a block prop. Stops at whitespace and at `)` / `]`, which close a markdown link target. */
const URL_RE = /https?:\/\/[^\s)\]<>"']+/g;

/** `/routepreview/<24 lowercase hex>` — a trip id. Anchored to the segment end so a longer hex run does not match. */
const ROUTE_PREVIEW_PATH_RE = /\/routepreview\/[0-9a-f]{24}(?:\/|$)/;

/** Sentence punctuation a bare URL in prose drags along ("…see https://…/routepreview/<id>."). */
const TRAILING_PUNCTUATION_RE = /[.,;:!?]+$/;

/**
 * The first `/routepreview/<tripId>` URL in `text`, exactly as authored (host and query kept), or null.
 * Exported for the `route-cta` block, which checks its own `href` the same way.
 */
export function routePreviewUrlIn(text: string): string | null {
	for (const match of text.matchAll(URL_RE)) {
		const candidate = match[0].replace(TRAILING_PUNCTUATION_RE, "");
		try {
			if (ROUTE_PREVIEW_PATH_RE.test(new URL(candidate).pathname)) return candidate;
		} catch {
			// Not a parsable URL: keep looking at the next candidate.
		}
	}
	return null;
}

/** The strings of one node that can carry a link: a block's `href` prop, a paragraph's text, a list's items. */
function linkBearingText(node: ArticleNode): string[] {
	if (node.kind === "block") return typeof node.props?.href === "string" ? [node.props.href] : [];
	if (node.kind === "para") return typeof node.text === "string" ? [node.text] : [];
	if (node.kind === "list") return Array.isArray(node.items) ? node.items.filter((item): item is string => typeof item === "string") : [];
	return [];
}

/**
 * The first `/routepreview/<tripId>` URL in the article, scanning `intro`, then each section's
 * `content`, then `outro`, in document order. Null when the article links no trip by id.
 */
export function articleTripHref(article: ArticleData): string | null {
	const nodes: ArticleNode[] = [...article.intro, ...article.sections.flatMap((section) => section.content), ...(article.outro ?? [])];
	for (const node of nodes) {
		for (const text of linkBearingText(node)) {
			const href = routePreviewUrlIn(text);
			if (href) return href;
		}
	}
	return null;
}

/** True when `href` (absolute or relative; query and hash ignored) points at `/routepreview/<tripId>`. */
export function isRoutePreviewHref(href: string): boolean {
	try {
		return ROUTE_PREVIEW_PATH_RE.test(new URL(href, "https://www.stippl.io").pathname);
	} catch {
		return false;
	}
}

/** `href` with `utm_content` set to `content` — replaces an existing one, keeps every other param. Returned unchanged if it is not a parsable URL. */
export function withUtmContent(href: string, content: string): string {
	try {
		const url = new URL(href);
		url.searchParams.set("utm_content", content);
		return url.toString();
	} catch {
		return href;
	}
}

/** `href` with `lang=<lang>` appended (query-safe, hash kept); unchanged for `"en"`. /routepreview is an unprefixed app route (a /xx/ prefix 301s to bare), so the language rides as a query param. */
export function withRoutePreviewLang(href: string, lang: string): string {
	if (lang === "en") return href;
	const hashAt = href.indexOf("#");
	const base = hashAt < 0 ? href : href.slice(0, hashAt);
	const hash = hashAt < 0 ? "" : href.slice(hashAt);
	return `${base}${base.includes("?") ? "&" : "?"}lang=${encodeURIComponent(lang)}${hash}`;
}
